<script setup lang="ts">
import BaseButton from '@/components/common/BaseButton.vue'
import { ref, onMounted, onUnmounted, computed, type ComponentPublicInstance } from 'vue'

interface Props {
  variant?: 'fill' | 'outline' | 'tonal' | 'shadow'
  size?: 'small' | 'medium' | 'large'
  color?: string
  fill?: boolean
  outline?: boolean
  tonal?: boolean
  shadow?: boolean
  raised?: boolean
  round?: boolean
  roundIos?: boolean
  roundMd?: boolean
  roundAurora?: boolean
  activeBgColor?: string
  activeTextColor?: string
  disabled?: boolean
  loading?: boolean
  magneticStrength?: number
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'fill',
  size: 'medium',
  magneticStrength: 0.15,
})

const buttonRef = ref<ComponentPublicInstance | null>(null)
const x = ref(0)
const y = ref(0)
const reduceMotion = ref(false)

let rafId: number | undefined

function getButtonElement() {
  const element = buttonRef.value?.$el
  return element instanceof HTMLElement ? element : null
}

function onMouseMove(e: MouseEvent) {
  if (reduceMotion.value || props.disabled || props.loading) return

  const rect = getButtonElement()?.getBoundingClientRect()
  if (!rect) return

  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2

  const targetX = (e.clientX - centerX) * props.magneticStrength
  const targetY = (e.clientY - centerY) * props.magneticStrength

  x.value += (targetX - x.value) * 0.25
  y.value += (targetY - y.value) * 0.25
}

function onMouseLeave() {
  if (reduceMotion.value) return
  x.value = 0
  y.value = 0
}

function onMouseDown() {
  const element = getButtonElement()
  if (element && !props.disabled && !props.loading) {
    element.style.transform = `translate(${x.value}px, ${y.value}px) scale(0.98)`
  }
}

function onMouseUp() {
  const element = getButtonElement()
  if (element && !props.disabled && !props.loading) {
    element.style.transform = `translate(${x.value}px, ${y.value}px) scale(1)`
  }
}

function applyTransform() {
  const element = getButtonElement()
  if (element && !reduceMotion.value) {
    element.style.transform = `translate(${x.value}px, ${y.value}px)`
  }
}

onMounted(() => {
  reduceMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const el = getButtonElement()
  if (!el) return

  if (reduceMotion.value) return

  el.addEventListener('mousemove', onMouseMove)
  el.addEventListener('mouseleave', onMouseLeave)
  el.addEventListener('mousedown', onMouseDown)
  el.addEventListener('mouseup', onMouseUp)
  el.addEventListener('click', onMouseUp)

  rafId = requestAnimationFrame(function loop() {
    applyTransform()
    rafId = requestAnimationFrame(loop)
  })
})

onUnmounted(() => {
  const el = getButtonElement()
  if (el) {
    el.removeEventListener('mousemove', onMouseMove)
    el.removeEventListener('mouseleave', onMouseLeave)
    el.removeEventListener('mousedown', onMouseDown)
    el.removeEventListener('mouseup', onMouseUp)
  }
  if (rafId !== undefined) cancelAnimationFrame(rafId)
})

const baseVariant = computed(() => {
  if (props.outline || props.variant === 'outline') return 'secondary'
  if (props.tonal || props.variant === 'tonal') return 'accent'
  return 'primary'
})

const baseSize = computed(() => {
  if (props.size === 'small') return 'sm'
  if (props.size === 'large') return 'lg'
  return 'md'
})

const buttonClasses = computed(() => [
  'relative overflow-hidden transition-transform duration-150 ease-out',
  (props.round || props.roundIos || props.roundMd || props.roundAurora) && '!rounded-full',
  (props.shadow || props.raised || props.variant === 'shadow') && 'shadow-lg shadow-cyan-500/20',
])
</script>

<template>
  <BaseButton
    ref="buttonRef"
    :variant="baseVariant"
    :size="baseSize"
    :disabled="props.disabled"
    :loading="props.loading"
    :class="buttonClasses"
  >
    <slot />
  </BaseButton>
</template>

<style scoped>
button {
  will-change: transform;
}

@media (prefers-reduced-motion: reduce) {
  button {
    transform: none !important;
  }
}
</style>
